import type { AddressData } from '@adyen/adyen-web'
import type { Environment } from 'types/environment'
import type * as Contact from 'types/models/contact.model'
import type * as Brink from 'types/vendors/brink'
import type { TaxForOrder } from '~/models/order/taxes'
import type { CheckoutInformation } from '~/types/checkout'
import type { CartItem, ShippingMethod } from '~/types/models/cart.model'
import type { Order } from '~/types/models/order'
import { WAREHOUSE_ERROR } from '~/configuration/global.configuration'
import { getAdyenClientKey, getAdyenEnvironment } from '~/lib/adyen'
import { convertToBrinkAddress, normalizeBrinkPrice, normalizeCartItems } from '~/lib/brink'
import { DEFAULT_CHECKOUT_INFORMATION } from '~/lib/checkout'
import { getMarket, getStorefront } from '~/lib/storefronts'
import * as applepay from '~/lib/vendors/apple/apple-pay'
import { normalizePostCartOrderToOrder } from '~/lib/vendors/brink/normalizePostCartOrderToOrder'
import { normalizePriceDiscount } from '~/lib/vendors/brink/normalizePriceDiscount'
import * as paypal from '~/lib/vendors/paypal/paypal'
import { getItemsCount } from '~/models/cart/getItemsCount'
import { getPriceProducts } from '~/models/cart/getPriceProducts'
import { getDefaultOrder } from '~/models/checkout/order'
import { DEFAULT_TAXES_OBJECT } from '~/models/order/taxes'
import { useLastRawCart } from './useLastRawCart'

function isAppleShippingMethod(option: any): option is ApplePayJS.ApplePayShippingMethod {
  return Reflect.has(option, 'identifier')
}

function isPaypalShippingMethod(option: any): option is Adyen.PayPalShippingMethodSelection {
  return Reflect.has(option, 'type')
}

export function useExpressOrder() {
  const order = useState('expressOrder', () => getDefaultOrder())
  const shippingMethods = useState<ShippingMethod[]>('expressShippingMethods', () => [])
  const checkoutInformation = useState<CheckoutInformation>('expressCheckoutInformation', () => DEFAULT_CHECKOUT_INFORMATION)
  const forceRefetchShipping = useState<boolean>('expressCartAmountChanged', () => false)
  const taxes = useState<TaxForOrder>('expressTaxes', () => (DEFAULT_TAXES_OBJECT))
  const selectedShippingMethod = useState<Brink.DeliveryMethod | null>('paypalSelectedShippingMethod', () => (null))
  const storefrontStore = useStorefrontStore()
  const { storefrontCode } = storefrontStore.current
  const { locale, countryCode } = storefrontStore.currentMarket
  const adyenSupportFunctions = useAdyenSupportFunctions()
  const languageCode = adyenSupportFunctions.getAdyenLanguage(locale.html)
  const {
    public: { adyenEnvironment, adyenClientKey, adyenClientKeyUs },
  } = useRuntimeConfig()
  const clientKey = getAdyenClientKey(storefrontCode, {
    us: adyenClientKeyUs,
    default: adyenClientKey,
  })
  const getEnvironment = () => getAdyenEnvironment(storefrontStore.currentMarketCountryCode, adyenEnvironment)

  // Private Functions
  function _setAddress(payload: { address: Contact.Address, information: Contact.Information }): void {
    const { address, information } = payload
    checkoutInformation.value = {
      address: {
        billing: address,
        shipping: address,
        useShippingForBilling: true,
      },
      contact: information,
    }

    order.value = {
      ...order.value,
      address: {
        billing: address,
        shipping: address,
      },
      contact: {
        firstName: address.firstName,
        lastName: address.lastName,
        email: information.email,
        telephone: information.telephone,
      },
    }
  }

  function _setShippingMethods(data: ShippingMethod[]): void {
    shippingMethods.value = data
  }

  function _getItems(): { items: CartItem[], applePayItems: ApplePayJS.ApplePayLineItem[] } {
    const items = order.value.items
    const applePayItems = items.map(applepay.normalizeCartItem)

    return {
      items,
      applePayItems,
    }
  }

  // Public Functions
  function setBillingAddress(payload: ApplePayJS.ApplePayPaymentContact | { authorizedEvent: any, address: Partial<AddressData> }, isApplePay: boolean = true): void {
    let address: Contact.Address
    if (isApplePay) {
      payload = payload as ApplePayJS.ApplePayPaymentContact
      address = applepay.normalizeAddress(payload).address
    }
    else {
      payload = payload as { authorizedEvent: any, address: Partial<AddressData> }
      address = paypal.normalizeAddress(payload).address
    }

    order.value.address!.billing = address
    checkoutInformation.value.address.billing = address
    checkoutInformation.value.address.useShippingForBilling = false
  }

  function setItems(_currency?: Environment.Currency['code']): void {
    const currency = _currency || order.value.currency
    const lastRawCart = useLastRawCart().lastRawCart.value

    if (!lastRawCart) {
      console.error('Express Payment - Cart not Found')
      return
    }

    const items = normalizeCartItems(lastRawCart.cartItems, currency, lastRawCart)
    const itemsCount = getItemsCount(items)
    const priceProducts = getPriceProducts(items)
    const priceTotal = priceProducts + order.value.priceShipping
    const priceDiscount = normalizePriceDiscount(lastRawCart, currency)

    if (priceProducts !== order.value.priceProducts) {
      forceRefetchShipping.value = true
    }
    order.value = {
      ...order.value,
      items,
      itemsCount,
      priceProducts,
      priceTotal,
      priceDiscount,
    }
  }

  async function setMarket(countryCode?: Environment.CountryCode): Promise<void> {
    if (countryCode) {
      const market = getMarket(countryCode)
      const currency = market.currency.code
      const storefront = getStorefront(market.storefrontCode)
      const currentMarket = order.value.market

      const isDifferentWarehouse = currentMarket.warehouse.region !== market.warehouse.region

      if (isDifferentWarehouse) {
        throw new Error(WAREHOUSE_ERROR)
      }

      const cartStore = useCartStore()
      await cartStore.updateCountryCode(countryCode)

      const isDifferentCurrency = order.value.currency !== currency

      if (isDifferentCurrency) {
        setItems(currency)
      }

      order.value = {
        ...order.value,
        market,
        currency,
        storefront,
      }
      return
    }

    const storefrontStore = useStorefrontStore()

    order.value = {
      ...order.value,
      market: storefrontStore.currentMarket,
      currency: storefrontStore.currency.code,
      storefront: storefrontStore.current,
    }
  }

  function setShippingAddress(payload: ApplePayJS.ApplePayPaymentContact | { authorizedEvent: any, address: Partial<AddressData> }, isApplePay: boolean = true): void {
    let addressData: { address: Contact.Address, information: Contact.Information }
    if (isApplePay) {
      payload = payload as ApplePayJS.ApplePayPaymentContact
      addressData = applepay.normalizeAddress(payload)
    }
    else {
      payload = payload as { authorizedEvent: any, address: Partial<AddressData> }
      addressData = paypal.normalizeAddress(payload, true)
    }

    _setAddress(addressData)
  }

  async function setShippingMethod(payload: ShippingMethod | ApplePayJS.ApplePayShippingMethod | Adyen.PayPalShippingMethodSelection): Promise<void> {
    const _shippingMethod = resolveShippingMethod(payload)

    if (!_shippingMethod) {
      return
    }

    const priceTotal = order.value.priceProducts + _shippingMethod.price
    order.value = {
      ...order.value,
      shippingMethod: {
        name: _shippingMethod.title,
        estimatedDeliveryTime: _shippingMethod.deliveryDetails,
      },
      priceShipping: _shippingMethod.price,
      priceTotal,
    }
  }

  function getAllAppleLineItems(): ApplePayJS.ApplePayLineItem[] {
    const { applePayItems } = _getItems()

    const discountLineItem = order.value.priceDiscount > 0
      ? [{
          label: 'Discount',
          amount: order.value.priceDiscount.toString(),
        }]
      : []
    const taxesLineItems = taxes.value.total > 0
      ? [{
          label: 'Taxes',
          amount: taxes.value.total.toString(),
        }]
      : []

    const applePayShippingMethod = getShippingMethod()?.applePayShippingMethod

    if (!applePayShippingMethod) {
      return [...applePayItems, ...taxesLineItems, ...discountLineItem]
    }

    const shippingLineItem = applepay.getShippingMethodLineItem(applePayShippingMethod)

    return [...applePayItems, shippingLineItem, ...taxesLineItems, ...discountLineItem]
  }

  function getBrinkBillingAddress(): Brink.OverrideAddress {
    const _checkoutInformation = checkoutInformation.value
    const contact = _checkoutInformation.contact
    const billingAddress: Brink.OverrideAddress = convertToBrinkAddress(_checkoutInformation.address.billing, contact)
    return billingAddress
  }

  function getBrinkEmail(): string {
    const _checkoutInformation = checkoutInformation.value
    const contact = _checkoutInformation.contact
    return contact.email
  }

  function getBrinkShippingAddress(): Brink.OverrideAddress {
    const _checkoutInformation = checkoutInformation.value
    const contact = _checkoutInformation.contact
    const shippingAddress: Brink.OverrideAddress = convertToBrinkAddress(_checkoutInformation.address.shipping, contact)
    return shippingAddress
  }

  function getNormalizedBillingAddress(): Contact.Address {
    const _checkoutInformation = checkoutInformation.value
    return _checkoutInformation.address.billing
  }

  function getNormalizedShippingAddress(): Contact.Address {
    const _checkoutInformation = checkoutInformation.value
    return _checkoutInformation.address.shipping
  }

  function getOrderID(): string {
    return order.value.id
  }

  function getShippingMethod(): { shippingMethod: ShippingMethod, applePayShippingMethod: ApplePayJS.ApplePayShippingMethod, PayPalShippingMethod: Brink.DeliveryMethod } | undefined {
    const shippingMethod = getShippingMethodByName(order.value.shippingMethod.name)

    if (!shippingMethod) {
      return
    }

    const applePayShippingMethod = applepay.normalizeShippingMethod(shippingMethod)
    const PayPalShippingMethod = paypal.normalizeShippingMethod(shippingMethod, order.value.currency)

    return {
      shippingMethod,
      applePayShippingMethod,
      PayPalShippingMethod,
    }
  }

  function getShippingMethodByName(name: string): ShippingMethod | undefined {
    return shippingMethods.value.find(method => method.title === name)
  }

  function getTotal(): { totalPrice: number, applePayTotal: ApplePayJS.ApplePayLineItem } {
    const { priceTotal } = order.value
    const taxAmount = taxes.value.total
    const totalPrice = priceTotal + taxAmount
    const applePayTotal = applepay.normalizeApplePayTotal(totalPrice)

    return {
      totalPrice,
      applePayTotal,
    }
  }

  function createConfig(amount: { currency: string, value: number }, paymentMethodsResponse: Brink.PaymentMethodsResponseBody) {
    const config = {
      paymentMethodsResponse,
      clientKey,
      environment: getEnvironment(),
      amount,
      countryCode,
      locale: languageCode,
    }
    return config
  }

  async function createOrder(): Promise<Order> {
    const context = useNuxtApp()
    const orderApplePay = useExpressOrder()
    const _checkoutInformation = checkoutInformation.value
    const contact = _checkoutInformation.contact
    const shippingAddress = convertToBrinkAddress(_checkoutInformation.address.shipping, contact)
    const billingAddress = convertToBrinkAddress(_checkoutInformation.address.billing, contact)
    const orderBody: Brink.CartsToOrdersRequest = {
      shippingAddress,
      billingAddress,
    }

    await orderApplePay.updateBrinkCart()
    const orderResponse = await context.$payments.createCartOrders(orderBody)

    const cart: CartState = useCartStore().state
    const _order = normalizePostCartOrderToOrder(orderResponse, cart, orderApplePay.getShippingMethod()?.shippingMethod)

    order.value = _order
    taxes.value = { total: _order.externalTaxAmount ? _order.externalTaxAmount : 0, lineItems: [] }
    return _order
  }

  async function fetchShippingMethods(_countryCode?: Environment.CountryCode): Promise<void> {
    const context = useNuxtApp()
    const countryCode = _countryCode || order.value.market.countryCode
    const market = order.value.market
    const shippingMethods = await context.$cart.listShippingMethods(countryCode, { market }) as ShippingMethod[]
    _setShippingMethods(shippingMethods)

    const currentShippingMethod = getShippingMethod()?.shippingMethod
    const isShippingMethodAvailable = currentShippingMethod && shippingMethods.some(method => (method.id === currentShippingMethod.id) && (method.price === currentShippingMethod.price))

    if (!isShippingMethodAvailable) {
      const defaultShippingMethod = shippingMethods[0]
      await setShippingMethod(defaultShippingMethod)
    }
  }

  function isDifferentWarehouse(): boolean {
    const cartStore = useCartStore()
    const countryCodeCart = cartStore.state.countryCode

    if (!countryCodeCart) {
      return true
    }

    const cartMarket = getMarket(countryCodeCart)
    const applePayMarket = order.value.market

    const isSameWarehouse = cartMarket.warehouse.region === applePayMarket.warehouse.region

    return !isSameWarehouse
  }

  function resetSelectedShippingMethod(): void {
    selectedShippingMethod.value = null
  }

  function resolveShippingMethod(option: ShippingMethod | ApplePayJS.ApplePayShippingMethod | Adyen.PayPalShippingMethodSelection): ShippingMethod {
    if (isAppleShippingMethod(option)) {
      return applepay.convertShippingMethod(option)
    }

    if (isPaypalShippingMethod(option)) {
      return paypal.convertShippingMethod(option)
    }

    return option
  }

  function saveOrder(_order?: Brink.Order, isApplePay: boolean = true): void {
    if (_order) {
      const orderStatus = _order.status ?? _order.statusLog[_order.statusLog.length - 1]?.status ?? order.value.status
      order.value = {
        ...order.value,
        id: _order.id,
        status: orderStatus,
        createdAt: _order.created ?? order.value.createdAt,
        updatedAt: _order.lastUpdated ?? order.value.updatedAt,
        priceTax: normalizeBrinkPrice(_order.orderTaxAmount, _order.currencyUnit),
        orderNumber: String(_order.reference),
        payment: {
          method: isApplePay ? 'ApplePayExpress' : 'PayPalExpress',
          status: orderStatus,
        },
        externalTaxAmount:
          _order.externalTaxRate && normalizeBrinkPrice(_order.orderTaxAmount, _order.currencyUnit),
      }
    }

    useOrderStore().setSession({
      session: {
        id: order.value.id,
        sessionData: 'placeholder',
      },
      order: order.value,
    })

    forceRefetchShipping.value = true
  }

  function shouldUpdateShippingMethods(country?: Environment.CountryCode): boolean {
    if (forceRefetchShipping.value) {
      forceRefetchShipping.value = false
      return true
    }

    if (shippingMethods.value.length) {
      return true
    }

    if (!country) {
      return false
    }

    return order.value.market.countryCode !== country
  }

  async function updateBrinkCart(): Promise<void> {
    const cartStore = useCartStore()
    const shippingMethod = getShippingMethod()?.shippingMethod

    if (!shippingMethod) {
      return
    }

    // Need to add shipping method last, any price updates will wipe out the shipping method from the brink cart
    await cartStore.addShippingMethodToCart(shippingMethod.id)
  }

  // Apple Pay
  function getAppleShippingMethods(): ApplePayJS.ApplePayShippingMethod[] {
    return shippingMethods.value.map(applepay.normalizeShippingMethod)
  }

  // PayPal
  async function createPayPalShippingMethodList(selectedId?: string): Promise<{ shippingMethodList: Brink.DeliveryMethod[], selectedShippingMethod: Brink.DeliveryMethod }> {
    const _paypalShippingMethodList = _getPayPalShippingMethods()
    let _selectedMethod: Brink.DeliveryMethod = _paypalShippingMethodList[0]

    if (selectedId) {
      _selectedMethod = _paypalShippingMethodList.filter(item => item.reference === selectedId)[0]
    }
    else if (!selectedId && selectedShippingMethod.value) {
      _selectedMethod = selectedShippingMethod.value
    }

    selectedShippingMethod.value = _selectedMethod
    return { shippingMethodList: _paypalShippingMethodList, selectedShippingMethod: _selectedMethod }
  }

  function _getPayPalShippingMethods(): Brink.DeliveryMethod[] {
    return shippingMethods.value.map((list) => { return paypal.normalizeShippingMethod(list, order.value.currency) })
  }

  return {
    order,
    checkoutInformation,

    getAllAppleLineItems,
    getAppleShippingMethods,
    getBrinkShippingAddress,
    getBrinkBillingAddress,
    getBrinkEmail,
    getNormalizedShippingAddress,
    getNormalizedBillingAddress,
    getOrderID,
    getShippingMethod,
    getTotal,

    setItems,
    setMarket,
    setShippingMethod,
    setBillingAddress,
    setShippingAddress,

    createConfig,
    createPayPalShippingMethodList,
    createOrder,
    fetchShippingMethods,
    isDifferentWarehouse,
    resetSelectedShippingMethod, // Should be using for apple pay as well?
    saveOrder,
    shouldUpdateShippingMethods,
    updateBrinkCart,
  }
}
